<template>
  <div class="dimensional-square-lingbao">
    <div class="table-header-actions">
      <h5 style="color: #fff"><span></span>次元广场灵宝（在线）</h5>

      <el-radio-group v-model="compareType" size="small">
        <el-radio-button label="standard">标准对比</el-radio-button>
        <el-radio-button label="baseline">基线对比</el-radio-button>
      </el-radio-group>
    </div>

    <el-table
      class="my-table-hok_v1"
      size="mini"
      :data="tableData"
      :header-cell-style="{
        background: 'rgb(35, 36, 72)',
        color: 'rgb(188, 189, 212)',
        'font-size': '14px',
        'font-family': 'Microsoft YaHei, Microsoft YaHei-Regular',
        'font-weight': 'normal',
      }"
      :span-method="cellMerge"
    >
      <!-- 画质 -->
      <el-table-column prop="quality" label="画质" width="150">
        <template #default="{ row }">
          <span>{{ row.model }}({{ row.quality || "/" }})</span>
        </template>
      </el-table-column>

      <!-- 最近更新时间 -->
      <el-table-column prop="date" label="最近更新时间" width="130">
        <template #default="{ row }">
          <span>{{ row.date || "--" }}</span>
        </template>
      </el-table-column>

      <!-- SVN -->
      <el-table-column prop="svn" label="SVN" width="100">
        <template #default="{ row }">
          <span>{{ row.svn || "--" }}</span>
        </template>
      </el-table-column>

      <!-- 帧率模式 -->
      <el-table-column prop="frame_rate" label="帧率模式" width="100">
        <template #default="{ row }">
          <span>{{ row.frame_rate || "/" }}</span>
        </template>
      </el-table-column>

      <!-- 结论 -->
      <el-table-column prop="result" label="结论" width="80">
        <template #default="{ row }">
          <span :style="{ color: getResultColor(row) }">{{ row.result }}</span>
        </template>
      </el-table-column>

      <!-- 指标列（标准 / 数值），按图2顺序遍历 -->
      <el-table-column
        v-for="metric in metricColumns"
        :key="metric.key"
        :label="metric.label"
      >
        <el-table-column label="标准" width="80">
          <template #default="{ row }">
            <span style="color: rgb(51, 157, 255)">{{
              row?.standard?.[metric.key] || "/"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column label="数值" width="80">
          <template #default="{ row }">
            <span :style="`color: ${getValueColor(row, metric.key)}`">{{
              row?.[metric.key] ?? "-"
            }}</span>
          </template>
        </el-table-column>
      </el-table-column>

      <!-- 链接 -->
      <el-table-column label="链接" width="80">
        <template #default="{ row }">
          <a
            v-if="row.url && row.url !== '--'"
            target="_blank"
            style="
              cursor: pointer;
              color: rgb(24, 144, 255);
              text-decoration: none;
            "
            :href="row.url"
            >查看</a
          >
          <span v-else>--</span>
        </template>
      </el-table-column>

      <!-- SVN 链接 -->
      <el-table-column label="SVN链接" width="90">
        <template #default="{ row }">
          <a
            v-if="row.svn_url && row.svn_url !== '--'"
            target="_blank"
            style="
              cursor: pointer;
              color: rgb(24, 144, 255);
              text-decoration: none;
            "
            :href="row.svn_url"
            >SVN链接</a
          >
          <span v-else>--</span>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import axios from "axios";
import { ElMessage } from "element-plus";

export default {
  name: "DimensionalSquareLingbao",
  props: {
    project: {
      type: [String, Number],
      default: 57,
    },
  },

  computed: {
    skinId() {
      return this.$route?.query?.skin_id;
    },
  },

  data() {
    return {
      compareType: "standard", // standard: 标准对比  baseline: 基线对比（仅UI呈现）
      tableData: [],
      originRes: {}, // 按机型/版本号存放的原始接口数据
      // 指标列：按图中"标准对比/基线对比"切换区域下方表格的列顺序排列
      // 其中 FD峰值(低于)、BigJank(/10min)(低于)、平均帧能耗(低于)
      // 为图3红色箭头标记的列，接口暂未返回对应数值，但列仍展示，数值列显示 "-"
      metricColumns: [
        { label: "平均帧率(高于)", key: "avg_fps" },
        { label: "帧率方差(低于)", key: "var_fps" },
        { label: "最低帧率(高于)", key: "min_fps" },
        { label: "CPU占用率均值(低于)", key: "avg_cpu" },
        { label: "内存峰值(低于)", key: "max_pss" },
        { label: "平均加载耗时(低于)", key: "avg_load_time" },
        { label: "FD峰值(低于)", key: "fd_peak" },
        { label: "Jank(/10min)(低于)", key: "jank" },
        { label: "BigJank(/10min)(低于)", key: "big_jank" },
        { label: "平均帧能耗(低于)", key: "avg_fps_power" },
      ],
    };
  },

  watch: {
    skinId() {
      this.fetchData();
    },
  },

  methods: {
    async fetchData() {
      if (!this.skinId) return;
      try {
        this.tableData = [];
        this.originRes = {};
        const { data } = await axios("/report/dimension_lingbao_data/", {
          params: {
            project: this.project,
            skin_id: this.skinId,
          },
        });

        // 接口格式与皮肤性能接口一致：data[model] = [entries...]
        for (const model in data) {
          const entries = data[model] || [];
          this.originRes[model] = entries;
          entries.forEach((v) => {
            if (v.level == 0) {
              this.tableData.push(this.buildRow(model, v));
            }
          });
        }
      } catch (e) {
        console.error("dimension_lingbao_data 请求失败", e);
        ElMessage.error("次元广场灵宝数据获取失败");
      }
    },

    buildRow(model, v) {
      return {
        ...v,
        model,
        quality: v.quality || "",
        date: v.date || "",
        svn: v.svn,
        frame_mode: v.frame_mode,
        result: v.result,
        url: v.url || "",
        svn_url: v["svn链接"] || v.svn_url || "",
        standard: v.standard || {},
      };
    },

    cellMerge({ row, column, rowIndex, columnIndex }) {
      if (columnIndex !== 0) return;
      const model = row.model;
      const rows = this.tableData;
      let startIdx = -1;
      let count = 0;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].model === model) {
          if (startIdx === -1) startIdx = i;
          count++;
        }
      }
      if (rowIndex === startIdx && count > 1) {
        return { rowspan: count, colspan: 1 };
      } else if (count > 1) {
        return { rowspan: 0, colspan: 0 };
      }
    },

    getResultColor(row) {
      const result = row.result;
      if (result == "达标") {
        return "rgb(121, 217, 127)";
      } else if (result == "不达标") {
        return "rgb(255, 76, 55)";
      }
    },

    getValueColor(row, key) {
      const color = row?.color?.[key];
      if (color == "green") {
        return "rgb(121, 217, 127);";
      } else if (color == "red") {
        return "rgb(255, 76, 55);";
      } else {
        return "";
      }
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.dimensional-square-lingbao {
  margin-top: 20px;
}

.table-header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.table-header-actions h5 {
  margin: 0;
}

.el-table {
  --el-bg-color: transparent;
}

.el-table :deep(th),
.el-table :deep(tr),
.el-table :deep(td),
.el-table :deep(tr:hover),
.el-table :deep(tr:hover td) {
  color: #fff;
  background-color: rgb(34, 37, 41);
  border-bottom: 1px solid #606469;
}

.el-table :deep(tr:hover td) {
  color: #4ed8da;
}

.my-table-hok_v1 :deep(.el-table__row .cell) {
  background: rgb(35, 36, 72);
  color: rgb(188, 189, 212);
  font-size: 14px;
  font-family: "Microsoft YaHei", "Microsoft YaHei-Regular";
}

/* 表头不换行 */
.my-table-hok_v1 :deep(th .cell) {
  white-space: nowrap;
}

.el-table :deep(.el-table__body-wrapper) {
  background-color: rgb(34, 37, 41);
}
</style>
